import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Eye, Clock } from "lucide-react";
import { SectionHead, Reveal } from "./effects";
import { shortFormProjects } from "../data/projects";

const Card = ({ p, i, onOpen }) => {
  const videoRef = useRef(null);
  const [hover, setHover] = useState(false);

  const enter = () => {
    setHover(true);
    videoRef.current?.play().catch(() => {});
  };
  const leave = () => {
    setHover(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
  };

  return (
    <Reveal delay={(i % 3) * 0.1}>
      <motion.div
        data-testid={`short-form-card-${p.slug}`}
        whileHover={{ y: -10 }}
        transition={{ type: "spring", stiffness: 220, damping: 20 }}
        onMouseEnter={enter}
        onMouseLeave={leave}
        onClick={() => onOpen(p)}
        className="video-card group relative rounded-2xl overflow-hidden border border-white/10 bg-[#0a0a0c] cursor-pointer hover:border-[#D4AF37]/60 hover:shadow-[0_20px_60px_rgba(212,175,55,0.25)] transition-all duration-500"
      >
        <div className="relative aspect-[9/16] max-h-[420px] w-full overflow-hidden">
          <img
            src={p.thumb}
            alt={p.title}
            loading="lazy"
            style={p.thumbPos ? { objectPosition: p.thumbPos } : undefined}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${hover ? "opacity-0" : "opacity-100"}`}
          />
          {p.video && (
            <video
              ref={videoRef}
              src={p.video}
              muted
              loop
              playsInline
              preload="none"
              className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-500 ${hover ? "opacity-100" : "opacity-0"}`}
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-transparent to-black/30 pointer-events-none" />
          <span className="absolute top-4 left-4 glass rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-[#F5DE81]">
            {p.category}
          </span>
          <span className="absolute top-4 right-4 glass rounded-full px-2.5 py-1 text-[10px] tracking-wider text-slate-300 flex items-center gap-1.5">
            <Clock size={10} /> {p.duration}
          </span>
          <div className="absolute bottom-0 left-0 right-0 p-5">
            <h3 className="font-display text-lg font-semibold leading-snug mb-2">{p.title}</h3>
            <div className="flex items-center gap-1.5 text-[11px] text-slate-400">
              <Eye size={12} className="text-[#D4AF37]" /> {p.views} views generated
            </div>
          </div>
        </div>
      </motion.div>
    </Reveal>
  );
};

export const ShortForm = () => {
  const [active, setActive] = useState(null);

  return (
    <section id="short-form" data-testid="short-form-section" className="relative py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <SectionHead
          testId="short-form"
          eyebrow="Short Form Content"
          title={
            <>
              Built for the <span className="text-gradient-blue">scroll-stopping</span> era
            </>
          }
          sub="Videos optimized for retention and engagement."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {shortFormProjects.map((p, i) => (
            <Card key={p.slug} p={p} i={i} onOpen={setActive} />
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="short-form-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[110] flex items-center justify-center bg-black/95 backdrop-blur-xl p-4"
            onClick={() => setActive(null)}
          >
            <motion.div
              initial={{ scale: 0.85, y: 40 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 30 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-md rounded-3xl overflow-hidden glow-border bg-[#0a0a0c]"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                data-testid="short-form-modal-close"
                aria-label="Close"
                onClick={() => setActive(null)}
                className="absolute top-4 right-4 z-10 glass rounded-full p-2.5 text-white hover:border-[#D4AF37] transition-colors"
              >
                <X size={18} />
              </button>
              {active.video ? (
                <video
                  src={active.video}
                  controls
                  autoPlay
                  playsInline
                  className="w-full max-h-[70vh] object-cover bg-black"
                />
              ) : (
                <img
                  src={active.thumb}
                  alt={active.title}
                  className="w-full max-h-[70vh] object-contain bg-black"
                />
              )}
              <div className="p-6">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#F5DE81] mb-2">{active.category}</div>
                <h3 className="font-display text-xl font-semibold mb-2">{active.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed">{active.description}</p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
